import type { DesignActivity, TurnProgress } from "../api/contract";

export interface BuildStep {
  label: string;
  state: "done" | "active" | "next";
  /** What the designer is doing right now, under the active "Choosing a look" step. */
  detail?: string;
}

const FIRST_BUILD: Array<{ label: string; stage: TurnProgress["stage"] | null }> = [
  { label: "Noted what you told me", stage: null },
  { label: "Writing your pages", stage: "chat" },
  { label: "Choosing a look", stage: "design" },
  { label: "Saving your private draft", stage: "apply" },
];

// An owner moving from a card site has told Stax nothing yet: it reads that site first.
const MIGRATION_FIRST_STEP = { label: "Reading your current site", stage: "browse" as const };

const ORDER: Array<TurnProgress["stage"]> = ["browse", "chat", "design", "apply", "done"];

/**
 * The first-build checklist, derived from the server's checkpointed turn stage. Returns null for
 * ordinary replies. A chat-stage turn may still turn out to be a question rather than a build; the
 * server only reports `firstBuild` once it knows.
 */
export function buildSteps(progress: TurnProgress | null | undefined): BuildStep[] | null {
  if (!progress?.firstBuild) return null;
  // Reading a page is part of working out what to build, unless reading it is a step of its own.
  const current = ORDER.indexOf(progress.stage === "browse" && !progress.migrating ? "chat" : progress.stage);
  const steps = progress.migrating ? [MIGRATION_FIRST_STEP, ...FIRST_BUILD.slice(1)] : FIRST_BUILD;
  return steps.map(({ label, stage }) => {
    const position = stage === null ? -1 : ORDER.indexOf(stage);
    const state = position < current ? "done" : position === current ? "active" : "next";
    // Once the preview shows a first look, the rest of the design step is improving it.
    const detail = state === "active" && stage === "design" ? (progress.firstLook?.length ? "Your first look is in the preview. Polishing it now…" : activityLabel(progress.activity)) : null;
    return { label, state, ...(detail ? { detail } : {}) };
  });
}

/** "Home" is the home page; any other page is named, never a file or route. */
const pageLabel = (page: string) => page.toLowerCase() === "home" ? "home page" : `${page} page`;

/**
 * The designer's live activity in the owner's words, or null when the server sent none (an older
 * server, or the design has only just begun). It reports what the designer last did, not a guess.
 */
export function activityLabel(activity: DesignActivity | null | undefined): string | null {
  switch (activity?.kind) {
    case "planning":
      return activity.last?.kind === "look" ? "Deciding what to change…" : activity.last?.kind === "review" ? "Planning the finishing touches…" : "Planning the next change…";
    case "look": return "Looking through your site…";
    case "write": return "Writing your pages…";
    case "edit": return activity.page ? `Updating your ${pageLabel(activity.page)}…` : "Updating your site…";
    case "style": return "Adjusting the styling…";
    case "script": return "Adjusting how it behaves…";
    case "remove": return activity.page ? `Removing the ${pageLabel(activity.page)}…` : "Removing a page…";
    case "content": return "Adding your new wording…";
    case "brand": return "Setting your colours and fonts…";
    case "check": return "Checking everything fits together…";
    case "fix": return "Fixing something it spotted…";
    case "review": return "Looking at your site on a phone and a laptop…";
    default: return null;
  }
}

const SIZE_WORDS: Record<string, string> = { desktop: "a desktop", tablet: "a tablet", phone: "a phone" };

/** "Looking at your About page on a phone and a desktop…"; the home page is "the home page", never a route. */
export function lookingLabel(looking: NonNullable<TurnProgress["looking"]>): string {
  const last = looking.page.split("/").filter(Boolean).at(-1)?.replace(/\.html?$/i, "").replace(/[-_]+/g, " ").trim();
  const page = last ? `your ${last.charAt(0).toUpperCase()}${last.slice(1)} page` : "the home page";
  const sizes = looking.sizes.map((size) => SIZE_WORDS[size]).filter(Boolean).join(" and ");
  return `Looking at ${page}${sizes ? ` on ${sizes}` : ""}…`;
}

/**
 * What the admin is doing right now, in the owner's terms: one steady line per stage. Lines that
 * rotate through guessed activities read as noise and can describe work that isn't happening.
 */
export function replyingLabel(progress: TurnProgress | null | undefined): string {
  if (progress?.looking) return lookingLabel(progress.looking);
  if (progress?.stage === "browse" && progress.reading?.length) {
    const [first, ...rest] = progress.reading;
    return `Looking at ${first}${rest.length ? ` and ${rest.length} more` : ""}…`;
  }
  if (progress?.firstBuild) return "Building your first draft…";
  switch (progress?.stage) {
    case "design":
      return activityLabel(progress.activity) ?? "Designing your change…";
    case "apply":
      return "Saving your draft…";
    default:
      return "Stax is working on it…";
  }
}
